import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  AssistSchemaImportFieldsInput,
  CreateSchemaDefinitionInput,
  PreviewAssistImportFieldsInput,
  UpdateSchemaDefinitionInput,
  AddFieldInput,
  UpdateFieldInput,
  RunExtractionPreviewInput,
  ImportFieldsInput,
  UpdateSchemaLoadTransformInput,
} from '@erp/rpc';

const QUERY_KEY_BASE = 'schema-definitions';

export function useListSchemaDefinitions(input?: {
  targetModel?: string;
  schemaType?: 'sql' | 'extraction';
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'list', input],
    queryFn: () => rpcClient.core.schemaDefinitions.list(input ?? {}),
  });
}

export function useGetSchemaDefinition(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'get', id],
    queryFn: () => rpcClient.core.schemaDefinitions.get({ id }),
    enabled: !!id,
  });
}

export function useCreateSchemaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSchemaDefinitionInput) =>
      rpcClient.core.schemaDefinitions.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateSchemaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSchemaDefinitionInput) =>
      rpcClient.core.schemaDefinitions.update(input),
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY_BASE, 'get', data.id], data);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useDeleteSchemaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpcClient.core.schemaDefinitions.delete({ id }),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [QUERY_KEY_BASE, 'get', id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useAddSchemaField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddFieldInput) =>
      rpcClient.core.schemaDefinitions.addField(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateSchemaField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFieldInput) =>
      rpcClient.core.schemaDefinitions.updateField(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useRemoveSchemaField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; schemaDefinitionId: string }) =>
      rpcClient.core.schemaDefinitions.removeField(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useImportSchemaFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportFieldsInput) =>
      rpcClient.core.schemaDefinitions.importFields(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useAssistEditSchema() {
  return useMutation({
    mutationFn: (input: AssistSchemaImportFieldsInput) =>
      rpcClient.core.schemaDefinitions.assistEditSchema(input),
  });
}

/** @deprecated Use useAssistEditSchema */
export function useAssistSchemaImportFields() {
  return useAssistEditSchema();
}

export function usePreviewAssistImportFields() {
  return useMutation({
    mutationFn: (input: PreviewAssistImportFieldsInput) =>
      rpcClient.core.schemaDefinitions.previewAssistImportFields(input),
  });
}

export function useReorderSchemaFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      schemaDefinitionId: string;
      parentFieldId?: string | null;
      fieldIds: string[];
    }) => rpcClient.core.schemaDefinitions.reorderFields(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function usePreviewJsonSchema(id: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'preview', id],
    queryFn: () => rpcClient.core.schemaDefinitions.previewJsonSchema({ id }),
    enabled: !!id,
  });
}

export function useRunExtractionPreview() {
  return useMutation({
    mutationFn: (input: RunExtractionPreviewInput) =>
      rpcClient.core.schemaDefinitions.runExtractionPreview(input),
  });
}

// Version hooks

export function usePublishVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (schemaDefinitionId: string) =>
      rpcClient.core.schemaDefinitions.publishVersion({ schemaDefinitionId }),
    onSuccess: (_, schemaDefinitionId) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', schemaDefinitionId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'versions', schemaDefinitionId],
      });
    },
  });
}

export function useListVersions(schemaDefinitionId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'versions', schemaDefinitionId],
    queryFn: () =>
      rpcClient.core.schemaDefinitions.listVersions({ schemaDefinitionId }),
    enabled: !!schemaDefinitionId,
  });
}

export function useGetVersion(schemaDefinitionId: string, versionId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'version', schemaDefinitionId, versionId],
    queryFn: () =>
      rpcClient.core.schemaDefinitions.getVersion({
        schemaDefinitionId,
        versionId,
      }),
    enabled: !!schemaDefinitionId && !!versionId,
  });
}

export function useActivateVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { schemaDefinitionId: string; versionId: string }) =>
      rpcClient.core.schemaDefinitions.activateVersion(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', variables.schemaDefinitionId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'versions', variables.schemaDefinitionId],
      });
    },
  });
}

export function useSchemaLoadTransform(schemaId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'loadTransform', schemaId],
    queryFn: () =>
      rpcClient.core.schemaDefinitions.getSchemaLoadTransform({ schemaId }),
    enabled: !!schemaId,
  });
}

export function useUpdateSchemaLoadTransform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSchemaLoadTransformInput) =>
      rpcClient.core.schemaDefinitions.updateSchemaLoadTransform(input),
    onSuccess: (data, variables) =>
      Promise.all([
        queryClient.setQueryData(
          [QUERY_KEY_BASE, 'loadTransform', variables.schemaId],
          data,
        ),
        // Links report whether their schema can be direct-loaded.
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listLinks.key(),
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.core.dataSources.listAllLinks.key(),
        }),
      ]),
  });
}
